import { useEffect, useCallback, useRef } from 'react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface KeyBinding {
  /** Human-readable label */
  label: string;
  /** Key combination: modifier+key, e.g. 'ctrl+shift+p' */
  keys: string;
  /** Action to execute */
  action: () => void;
  /** Prevent default browser behaviour (default: true) */
  preventDefault?: boolean;
  /** Only active when editor is NOT focused (default: false) */
  globalOnly?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Key matching                                                       */
/* ------------------------------------------------------------------ */

function normaliseKey(k: string): string {
  return k.toLowerCase().replace('meta', 'ctrl');
}

function matchesBinding(e: KeyboardEvent, binding: string): boolean {
  const parts = binding.toLowerCase().split('+').map((p) => p.trim());

  const needsCtrl = parts.includes('ctrl') || parts.includes('meta');
  const needsShift = parts.includes('shift');
  const needsAlt = parts.includes('alt');
  const key = parts.filter((p) => !['ctrl', 'meta', 'shift', 'alt'].includes(p))[0];

  if (!key) return false;

  const ctrlOk = needsCtrl ? (e.ctrlKey || e.metaKey) : !(e.ctrlKey || e.metaKey);
  const shiftOk = needsShift ? e.shiftKey : !e.shiftKey;
  const altOk = needsAlt ? e.altKey : !e.altKey;
  const keyOk = normaliseKey(e.key) === key || e.code.toLowerCase() === `key${key}`;

  return ctrlOk && shiftOk && altOk && keyOk;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Register global keyboard shortcuts.
 * Shortcuts are automatically cleaned up on unmount.
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts([
 *   { label: 'Save', keys: 'ctrl+s', action: () => saveFile() },
 *   { label: 'Toggle Terminal', keys: 'ctrl+`', action: () => toggleTerm() },
 * ]);
 * ```
 */
export function useKeyboardShortcuts(bindings: KeyBinding[]) {
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;

  const handler = useCallback((e: KeyboardEvent) => {
    // Skip if user is typing in an input/textarea (unless modifier key combo)
    const target = e.target as HTMLElement;
    const isTextInput =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    for (const binding of bindingsRef.current) {
      if (!matchesBinding(e, binding.keys)) continue;

      // Skip non-modifier shortcuts when typing
      if (isTextInput && !binding.keys.includes('ctrl') && !binding.keys.includes('meta')) continue;

      if (binding.preventDefault !== false) {
        e.preventDefault();
        e.stopPropagation();
      }

      binding.action();
      return;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [handler]);
}

/* ------------------------------------------------------------------ */
/*  Shortcut reference list (for display)                              */
/* ------------------------------------------------------------------ */

export const IDE_SHORTCUTS: { category: string; shortcuts: { label: string; keys: string }[] }[] = [
  {
    category: 'File',
    shortcuts: [
      { label: 'Save File', keys: 'Ctrl+S' },
      { label: 'New File', keys: 'Ctrl+N' },
    ],
  },
  {
    category: 'View',
    shortcuts: [
      { label: 'Command Palette', keys: 'Ctrl+Shift+P' },
      { label: 'Toggle Terminal', keys: 'Ctrl+`' },
      { label: 'Toggle Sidebar', keys: 'Ctrl+B' },
      { label: 'Explorer', keys: 'Ctrl+Shift+E' },
      { label: 'Deploy Panel', keys: 'Ctrl+Shift+D' },
      { label: 'AI Assistant', keys: 'Ctrl+Shift+A' },
      { label: 'Health Monitor', keys: 'Ctrl+Shift+H' },
    ],
  },
  {
    category: 'Build',
    shortcuts: [
      { label: 'Compile Contract', keys: 'Ctrl+Shift+B' },
      { label: 'Deploy Contract', keys: 'Ctrl+Shift+Enter' },
    ],
  },
  {
    category: 'AI',
    shortcuts: [
      { label: 'AI Generate', keys: 'Ctrl+Shift+G' },
      { label: 'AI Explain', keys: 'Ctrl+Shift+X' },
    ],
  },
];
